import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useTableSort } from '../hooks/useTableSort';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface League {
  id: string;
  name: string;
  teams: { id: string; name: string; teamNumber: string }[];
  teamStatistics?: Array<{
    teamId: string;
    wins: number;
    losses: number;
    draws: number;
    goalsFor: number;
    goalsAgainst: number;
  }>;
}

interface Season {
  id: string;
  leagueId: string;
  name: string;
  startDate: string;
  endDate: string;
  isDraft?: boolean;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  colors: { primary: string; secondary: string };
  status: string;
  memberCount: number;
  manager: { id: string; firstName: string; lastName: string } | null;
  leagueId?: string;
  league?: { id: string; name: string } | null;
  club?: { id: string; name: string } | null;
}

const Leagues: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [filteredLeagues, setFilteredLeagues] = useState<League[]>([]);
  const [myLeagues, setMyLeagues] = useState<League[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [leagueTeams, setLeagueTeams] = useState<Team[]>([]);
  const [leagueSeasons, setLeagueSeasons] = useState<Record<string, Season[]>>({});
  const [startingSoonLeagueIds, setStartingSoonLeagueIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });

  // Prepare standings data for sorting
  const standingsData = selectedLeague && selectedLeague.teams && Array.isArray(selectedLeague.teams) && selectedLeague.teams.length > 0
    ? selectedLeague.teams.map((team) => {
        const stat = selectedLeague.teamStatistics?.find((s) => s.teamId === team.id) || {
          teamId: team.id,
          leagueId: selectedLeague.id,
          wins: 0,
          losses: 0,
          draws: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          points: 0,
          goalDifference: 0
        };
        const points = stat.wins * 3 + stat.draws;
        const goalDiff = stat.goalsFor - stat.goalsAgainst;
        return { ...stat, team, teamName: team.name, points, goalDifference: goalDiff };
      })
    : [];

  const { sortedData: sortedStandings, handleSort: handleStandingsSort, getSortIcon: getStandingsSortIcon } = useTableSort(standingsData, { key: 'points', direction: 'desc' });

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        // First, fetch all seasons to determine which leagues are in current season or starting soon
        let activeLeagueIds: Set<string> = new Set();
        let startingSoonIds: Set<string> = new Set();
        try {
          const seasonsResponse = await api.get('/seasons', { params: { page: '1', limit: '100' } });
          const allSeasons = seasonsResponse.data.seasons || seasonsResponse.data || [];
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Calculate 60 days from now
          const sixtyDaysFromNow = new Date();
          sixtyDaysFromNow.setDate(today.getDate() + 60);
          sixtyDaysFromNow.setHours(23, 59, 59, 999);
          
          // Find seasons that are currently active (not drafts, and today is between startDate and endDate)
          const activeSeasons = allSeasons.filter((season: Season) => {
            if (season.isDraft) return false;
            if (!season.startDate || !season.endDate) return false;
            
            const startDate = new Date(season.startDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(season.endDate);
            endDate.setHours(23, 59, 59, 999);
            
            return today >= startDate && today <= endDate;
          });
          
          // Find seasons starting within 60 days (not drafts, not currently active, startDate is in the future but within 60 days)
          const startingSoonSeasons = allSeasons.filter((season: Season) => {
            if (season.isDraft) return false;
            if (!season.startDate) return false;
            
            const startDate = new Date(season.startDate);
            startDate.setHours(0, 0, 0, 0);
            
            // Must be in the future, within 60 days, and not currently active
            const isFuture = startDate > today;
            const isWithin60Days = startDate <= sixtyDaysFromNow;
            const isNotActive = !activeSeasons.some((s: Season) => s.id === season.id);
            
            return isFuture && isWithin60Days && isNotActive;
          });
          
          // Get unique league IDs from active seasons
          activeLeagueIds = new Set(activeSeasons.map((season: Season) => season.leagueId));
          
          // Get unique league IDs from starting soon seasons
          startingSoonIds = new Set(startingSoonSeasons.map((season: Season) => season.leagueId));
          
          // Debug logging
          console.log('Active seasons:', activeSeasons.length, activeLeagueIds);
          console.log('Starting soon seasons:', startingSoonSeasons.length, startingSoonIds);
          console.log('Today:', today.toISOString().split('T')[0]);
          console.log('60 days from now:', sixtyDaysFromNow.toISOString().split('T')[0]);
          
          // Store seasons by league for display
          const seasonsByLeague: Record<string, Season[]> = {};
          allSeasons.forEach((season: Season) => {
            if (!seasonsByLeague[season.leagueId]) {
              seasonsByLeague[season.leagueId] = [];
            }
            seasonsByLeague[season.leagueId].push(season);
          });
          setLeagueSeasons(seasonsByLeague);
          setStartingSoonLeagueIds(startingSoonIds);
        } catch (error) {
          console.error('Error fetching seasons:', error);
        }
        
        // Fetch all leagues (or a large number) to ensure we get all leagues with active seasons
        const response = await api.get('/leagues', {
          params: {
            page: '1',
            limit: '100'
          }
        });
        const leaguesData = response.data.leagues || response.data;
        
        // Filter to show leagues that have an active season OR a season starting soon
        const leaguesInCurrentSeason = leaguesData.filter((league: League) => 
          activeLeagueIds.has(league.id) || startingSoonIds.has(league.id)
        );
        
        // Debug logging
        console.log('Total leagues fetched:', leaguesData.length);
        console.log('Leagues in current/starting soon season:', leaguesInCurrentSeason.length);
        console.log('Active league IDs:', Array.from(activeLeagueIds));
        console.log('Starting soon league IDs:', Array.from(startingSoonIds));
        
        // Store all filtered leagues (pagination will be handled by useEffect)
        setLeagues(leaguesInCurrentSeason);
        
        // Get user's associated leagues (leagues where user's teams participate)
        // Use the player profile endpoint to get team memberships in a single call
        if (user) {
          try {
            const userTeamIds = new Set<string>();

            // For managers, check the teams list (already have league teams data)
            if (user.profileType === 'manager' || isAdminOrSuperAdmin(user.profileType)) {
              leaguesInCurrentSeason.forEach((league: League) => {
                league.teams.forEach((team: any) => {
                  // We'll also check via player endpoint below
                });
              });
            }

            // Single call to get the user's team memberships
            try {
              const playerRes = await api.get(`/players/${user.id}`);
              const playerTeams = playerRes.data.teams || [];
              playerTeams.forEach((t: any) => userTeamIds.add(t.teamId));
            } catch {
              // User may not be a player — try teams list for managers
              const teamsRes = await api.get('/teams', { params: { page: '1', limit: '100' } });
              const allTeams = teamsRes.data.teams || teamsRes.data;
              allTeams.forEach((team: any) => {
                if (team.manager?.id === user.id) {
                  userTeamIds.add(team.id);
                }
              });
            }

            // Find leagues that contain user's teams AND are in current season
            const associatedLeagues = leaguesInCurrentSeason.filter((league: League) => {
              return league.teams.some((team) => userTeamIds.has(team.id));
            });

            setMyLeagues(associatedLeagues);
          } catch (error) {
            console.error('Error fetching user associations:', error);
          }
        }
        
        // Check if there's a league ID in the URL query params
        const leagueId = searchParams.get('leagueId');
        if (leagueId && (activeLeagueIds.has(leagueId) || startingSoonIds.has(leagueId))) {
          // Always fetch fresh data for the selected league (only if it's in current season or starting soon)
          fetchLeagueDetails(leagueId);
        } else if (leaguesInCurrentSeason.length > 0) {
          // Default to first league if no leagueId in URL (only from current season or starting soon leagues)
          fetchLeagueDetails(leaguesInCurrentSeason[0].id);
        }
      } catch (error) {
        console.error('Error fetching leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, [currentPage, searchParams, user]);
  
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filter leagues based on search term and apply pagination
  useEffect(() => {
    let filtered = leagues;
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = leagues.filter(league =>
        league.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply pagination
    const pageNum = parseInt(currentPage.toString()) || 1;
    const limitNum = 100;
    const startIndex = (pageNum - 1) * limitNum;
    const endIndex = startIndex + limitNum;
    const paginated = filtered.slice(startIndex, endIndex);
    
    setFilteredLeagues(paginated);
    
    // Update pagination info
    setPagination({
      page: pageNum,
      limit: limitNum,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / limitNum)
    });
  }, [searchTerm, leagues, currentPage]);

  // Refetch league details when searchParams change (e.g., when leagueId in URL changes)
  useEffect(() => {
    const leagueId = searchParams.get('leagueId');
    if (leagueId) {
      // Only fetch if we don't have this league selected or if it's different
      if (!selectedLeague || selectedLeague.id !== leagueId) {
        fetchLeagueDetails(leagueId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchLeagueDetails = async (leagueId: string) => {
    try {
      // Always fetch fresh data from the API
      const response = await api.get(`/leagues/${leagueId}`);
      const leagueData = response.data;
      
      // Ensure teams array exists and is an array
      if (!Array.isArray(leagueData.teams)) {
        console.warn(`League ${leagueId} has no teams array. Data:`, leagueData);
        leagueData.teams = [];
      }
      
      // Ensure teamStatistics array exists
      if (!Array.isArray(leagueData.teamStatistics)) {
        leagueData.teamStatistics = [];
      }
      
      // Log for debugging
      console.log(`Fetched league ${leagueId}:`, { 
        name: leagueData.name, 
        teamsCount: leagueData.teams?.length || 0,
        teamIds: leagueData.teamIds || [],
        teamStatisticsCount: leagueData.teamStatistics?.length || 0
      });
      
      // Always set the fresh data
      setSelectedLeague(leagueData);
      
      // Fetch full team details for the teams in this league
      if (leagueData.teams && leagueData.teams.length > 0) {
        fetchLeagueTeamsDetails(leagueData.teams.map((t: any) => t.id));
      } else {
        setLeagueTeams([]);
      }
      
      // Update URL with leagueId query parameter
      setSearchParams({ leagueId });
    } catch (error) {
      console.error('Error fetching league details:', error);
      // On error, try to clear the selected league or show error state
      setSelectedLeague(null);
      setLeagueTeams([]);
    }
  };

  const fetchLeagueTeamsDetails = async (teamIds: string[]) => {
    setLoadingTeams(true);
    try {
      // Fetch full details for each team in parallel (skip redundant league re-fetch)
      const teamsData = await Promise.all(
        teamIds.map(async (teamId) => {
          try {
            const teamResponse = await api.get(`/teams/${teamId}`);
            const team = teamResponse.data;
            // We already know the league from selectedLeague — no need to re-fetch
            if (selectedLeague) {
              team.league = { id: selectedLeague.id, name: selectedLeague.name };
            }
            return team;
          } catch (error) {
            console.error(`Error fetching team ${teamId}:`, error);
            return null;
          }
        })
      );
      setLeagueTeams(teamsData.filter((team): team is Team => team !== null));
    } catch (error) {
      console.error('Error fetching league teams details:', error);
      setLeagueTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Leagues</h1>
        {isAdminOrSuperAdmin(user?.profileType) && (
          <button className="btn btn-primary" onClick={() => navigate('/leagues/create')}>
            Create New League
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="form-group">
          <label htmlFor="league-search">Search Leagues</label>
          <input
            id="league-search"
            type="text"
            placeholder="Search by league name or season..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '10px' }}
          />
        </div>
        {searchTerm && (
          <p style={{ color: '#757575', fontSize: '14px', marginTop: '10px' }}>
            Showing {filteredLeagues.length} league(s) matching "{searchTerm}"
          </p>
        )}
      </div>

      {myLeagues.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h2>My Leagues</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Leagues where your teams participate
          </p>
          <div className="grid" style={{ marginBottom: '20px' }}>
            {myLeagues.map((league) => (
              <div
                key={league.id}
                className="card"
                style={{ cursor: 'pointer', borderColor: selectedLeague?.id === league.id ? 'var(--color-primary)' : undefined }}
                onClick={() => fetchLeagueDetails(league.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h3 style={{ margin: 0, marginBottom: '5px' }}>{league.name}</h3>
                    {leagueSeasons[league.id] && leagueSeasons[league.id].length > 0 && (() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      
                      // Find active season or upcoming season
                      const seasons = leagueSeasons[league.id]
                        .filter(s => !s.isDraft && s.startDate)
                        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                      
                      const activeSeason = seasons.find(s => {
                        const startDate = new Date(s.startDate);
                        startDate.setHours(0, 0, 0, 0);
                        const endDate = new Date(s.endDate);
                        endDate.setHours(23, 59, 59, 999);
                        return today >= startDate && today <= endDate;
                      });
                      
                      const upcomingSeason = seasons.find(s => {
                        const startDate = new Date(s.startDate);
                        startDate.setHours(0, 0, 0, 0);
                        return startDate > today;
                      });
                      
                      const seasonToShow = activeSeason || upcomingSeason;
                      
                      if (seasonToShow) {
                        return (
                          <>
                            <p><strong>{activeSeason ? 'Current Season' : 'Upcoming Season'}:</strong> {seasonToShow.name}</p>
                            <p><strong>Start:</strong> {new Date(seasonToShow.startDate).toLocaleDateString()}</p>
                            {activeSeason && <p><strong>End:</strong> {new Date(seasonToShow.endDate).toLocaleDateString()}</p>}
                          </>
                        );
                      }
                      return null;
                    })()}
                    <p><strong>Teams:</strong> {league.teams.length}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                    {isAdminOrSuperAdmin(user?.profileType) && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '5px 10px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/leagues/${league.id}/manage`);
                        }}
                      >
                        Manage
                      </button>
                    )}
                    {startingSoonLeagueIds.has(league.id) && (
                      <span style={{ 
                        backgroundColor: '#FFC107', 
                        color: '#78350f', 
                        padding: '2px 8px', 
                        borderRadius: '8px', 
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        display: 'inline-block',
                        width: '100%'
                      }}>
                        Starting Soon
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>All Leagues ({pagination.total > 0 ? pagination.total : filteredLeagues.length} leagues)</h2>
        {pagination.totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="grid" style={{ marginBottom: '30px' }}>
        {filteredLeagues.map((league) => (
          <div
            key={league.id}
            className="card"
            style={{ cursor: 'pointer', borderColor: selectedLeague?.id === league.id ? 'var(--color-primary)' : undefined }}
            onClick={() => fetchLeagueDetails(league.id)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ margin: 0, marginBottom: '5px' }}>{league.name}</h3>
                {leagueSeasons[league.id] && leagueSeasons[league.id].length > 0 && (() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  // Find active season or upcoming season
                  const seasons = leagueSeasons[league.id]
                    .filter(s => !s.isDraft && s.startDate)
                    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                  
                  const activeSeason = seasons.find(s => {
                    const startDate = new Date(s.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(s.endDate);
                    endDate.setHours(23, 59, 59, 999);
                    return today >= startDate && today <= endDate;
                  });
                  
                  const upcomingSeason = seasons.find(s => {
                    const startDate = new Date(s.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    return startDate > today;
                  });
                  
                  const seasonToShow = activeSeason || upcomingSeason;
                  
                  if (seasonToShow) {
                    return (
                      <>
                        <p><strong>{activeSeason ? 'Current Season' : 'Upcoming Season'}:</strong> {seasonToShow.name}</p>
                        <p><strong>Start:</strong> {new Date(seasonToShow.startDate).toLocaleDateString()}</p>
                        {activeSeason && <p><strong>End:</strong> {new Date(seasonToShow.endDate).toLocaleDateString()}</p>}
                      </>
                    );
                  }
                  return null;
                })()}
                <p><strong>Teams:</strong> {league.teams.length}</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                {isAdminOrSuperAdmin(user?.profileType) && (
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '12px', padding: '5px 10px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/leagues/${league.id}/manage`);
                    }}
                  >
                    Manage
                  </button>
                )}
                {startingSoonLeagueIds.has(league.id) && (
                  <span style={{ 
                    backgroundColor: '#FFC107', 
                    color: '#78350f', 
                    padding: '2px 8px', 
                    borderRadius: '8px', 
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    Starting Soon
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.totalPages} (Showing {filteredLeagues.length} of {pagination.total} leagues)
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pagination.totalPages}
          >
            Next
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={currentPage >= pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}

      {selectedLeague && (
        <div className="card">
          <div style={{ marginBottom: '15px' }}>
            <h2>{selectedLeague.name} - Standings</h2>
          </div>
          {selectedLeague.teams && Array.isArray(selectedLeague.teams) && selectedLeague.teams.length > 0 ? (
            <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('teamName' as any)}>
                    Team {getStandingsSortIcon('teamName' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('wins' as any)}>
                    Wins {getStandingsSortIcon('wins' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('losses' as any)}>
                    Losses {getStandingsSortIcon('losses' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('draws' as any)}>
                    Draws {getStandingsSortIcon('draws' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('goalsFor' as any)}>
                    Goals For {getStandingsSortIcon('goalsFor' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('goalsAgainst' as any)}>
                    Goals Against {getStandingsSortIcon('goalsAgainst' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('goalDifference' as any)}>
                    Goal Difference {getStandingsSortIcon('goalDifference' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStandingsSort('points' as any)}>
                    Points {getStandingsSortIcon('points' as any)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStandings.map((stat) => (
                  <tr key={stat.teamId}>
                    <td><strong>{stat.team?.name || 'Unknown'}</strong></td>
                    <td>{stat.wins}</td>
                    <td>{stat.losses}</td>
                    <td>{stat.draws}</td>
                    <td>{stat.goalsFor}</td>
                    <td>{stat.goalsAgainst}</td>
                    <td>{stat.goalDifference > 0 ? '+' : ''}{stat.goalDifference}</td>
                    <td><strong>{stat.points}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <p>No teams in this league yet.</p>
          )}
        </div>
      )}

      {selectedLeague && selectedLeague.teams && Array.isArray(selectedLeague.teams) && selectedLeague.teams.length > 0 && (
        <div className="card" style={{ marginTop: '30px' }}>
          <h2>{selectedLeague.name} - Teams</h2>
          {loadingTeams ? (
            <p style={{ marginTop: '20px' }}>Loading team details...</p>
          ) : leagueTeams.length > 0 ? (
            <div className="grid" style={{ marginTop: '20px' }}>
              {leagueTeams.map((team) => (
                <div key={team.id} className="team-card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/teams/${team.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: team.colors.primary,
                        border: `3px solid ${team.colors.secondary}`
                      }}
                    />
                    <div>
                      <h3>{team.name}</h3>
                      <p style={{ color: '#757575', fontSize: '14px' }}>{team.teamNumber}</p>
                    </div>
                  </div>
                  <p><strong>Status:</strong> <span className={`badge ${team.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{team.status}</span></p>
                  <p><strong>Members:</strong> {team.memberCount}</p>
                  {team.club && (
                    <p>
                      <strong>Club:</strong>{' '}
                      <Link 
                        to={`/clubs/${team.club.id}`} 
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                      >
                        {team.club.name}
                      </Link>
                    </p>
                  )}
                  {team.league && (
                    <p>
                      <strong>League:</strong>{' '}
                      <Link 
                        to={`/leagues?leagueId=${team.league.id}`} 
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                      >
                        {team.league.name}
                      </Link>
                    </p>
                  )}
                  {team.manager && (
                    <p><strong>Manager:</strong> {team.manager.firstName} {team.manager.lastName}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ marginTop: '20px' }}>No team details available.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Leagues;

